import axios from 'axios'
import { useAuthStore } from '@/pinia/auth'
import { HTTPClient, HTTPFileClient, HTTPGatewayClient, HTTPKeyCloakClient } from './http-client'

const cancel = {} // AbortControllers used to cancel a previous in-flight request to the same endpoint

function hashCode(s) {
  let hash = 0
  for (let i = 0; i < s.length; i++) {
    hash = (Math.imul(31, hash) + s.charCodeAt(i)) | 0
  }
  return hash
}

export async function deleteRequest(endpoint, signal, timeout = 0) {
  await HTTPClient.delete(endpoint, { signal, timeout })
}

export async function deleteRequestGateway(endpoint, timeout = 0, headers = {}) {
  try {
    const res = await HTTPGatewayClient.delete(endpoint, { timeout, headers })
    return res
  } catch (e: any) {
    throw new Error(e.message, { cause: e })
  }
}

export async function deleteRequestFile(endpoint, _body, signal, timeout = 0) {
  try {
    const res = await HTTPClient.delete(endpoint, { signal, timeout })
    return res
  } catch (e: any) {
    throw new Error(e.message, { cause: e })
  }
}

export async function getRequest<T>(endpoint, signal, timeout = 0, retry = 1) {
  // retry added to handle failed requests,
  // default value 1 so that retry parameter conveys how many times to retry.
  // requests without retry are not affected
  if (retry < 1) throw new Error('retry must be >= 1')
  const headers = {}
  try {
    const res = await HTTPClient.get<T>(endpoint, { signal, timeout, headers })
    return res
  } catch (e: any) {
    if (retry > 1) {
      return getRequest(endpoint, signal, timeout, retry - 1)
    } else {
      throw e
    }
  }
}

export async function getRequestGateway<T>(
  endpoint: string,
  timeout = 0,
  retry = 1,
  params = {},
  signal: AbortSignal | undefined = undefined,
  headers = {}
) {
  // retry added to handle failed requests,
  // default value 1 so that retry parameter conveys how many times to retry.
  // requests without retry are not affected
  if (retry < 1) throw new Error('retry must be >= 1')
  try {
    const res = await HTTPGatewayClient.get<T>(endpoint, {
      signal,
      timeout,
      headers,
      params,
      paramsSerializer: {
        indexes: null // no brackets at all
      }
    })
    return res
  } catch (e: any) {
    if (retry > 1) {
      return getRequestGateway(endpoint, timeout, retry - 1, params, signal, headers)
    } else {
      throw e
    }
  }
}

export async function getRequestKeyCloak(endpoint, signal, timeout = 0) {
  try {
    const res = await HTTPKeyCloakClient.get(endpoint, { signal, timeout })
    return res
  } catch (e: any) {
    throw new Error(e.message, { cause: e })
  }
}

export async function getRequestFile(endpoint, signal, timeout = 0) {
  try {
    const headers = {}
    const res = await HTTPFileClient.get(endpoint, {
      signal,
      timeout,
      responseType: 'blob',
      headers
    })
    return res
  } catch (e: any) {
    throw new Error(e.message, { cause: e })
  }
}

// if cancelPendingRequest is true a new request will cancel previous request if a response not yet is made
export async function getRequestv1(endpoint, cancelPendingRequest, timeOut = 0) {
  const url = globalThis.appConfig.VITE_API_BASE_URL + endpoint

  const headers = {
    Authorization: 'Bearer ' + useAuthStore().accessToken
  }
  axios.defaults.timeout = 0
  if (timeOut) axios.defaults.timeout = timeOut
  if (!cancelPendingRequest) {
    try {
      const data = await axios.get(url, {
        headers: headers
      })
      return data
    } catch (e) {
      console.error(e)
    }
  } else if (cancelPendingRequest) {
    try {
      const code = hashCode(endpoint.substring(0, endpoint.indexOf('?')))
      if (cancel[code]) {
        cancel[code].abort('Intercept earlier request')
      }
      const controller = new AbortController()
      cancel[code] = controller
      const data = await axios.get(url, {
        headers: headers,
        signal: controller.signal
      })
      return data
    } catch (e) {
      if (!axios.isCancel(e)) {
        throw e
      }
    }
  }
}

export async function patchRequestGateway(endpoint, body, signal, timeout = 0, headers = {}) {
  try {
    const res = await HTTPGatewayClient.patch(endpoint, body, { signal, timeout, headers })
    return res
  } catch (e: any) {
    throw new Error(e.message, { cause: e })
  }
}
export async function postRequest(endpoint, body, signal, timeout = 0) {
  try {
    const res = await HTTPClient.post(endpoint, body, { signal, timeout })
    return res
  } catch (e: any) {
    throw new Error(e.message, { cause: e })
  }
}
export async function postRequestGateway<T = any>(
  endpoint,
  body,
  signal: AbortSignal | undefined = undefined,
  timeout = 0,
  headers = {}
) {
  const res = await HTTPGatewayClient.post<T>(endpoint, body, { signal, timeout, headers })
  return res
}
export async function putRequestGateway(endpoint, body, timeout = 0) {
  try {
    const res = await HTTPGatewayClient.put(endpoint, body, { timeout })
    return res
  } catch (e: any) {
    throw new Error(e.message, { cause: e })
  }
}

export async function putRequest(endpoint, body, signal, timeout = 0) {
  try {
    const res = await HTTPClient.put(endpoint, body, { signal, timeout })
    return res
  } catch (e: any) {
    throw new Error(e.message, { cause: e })
  }
}

export async function patchRequestv1(endpoint, content) {
  const url = globalThis.appConfig.VITE_API_BASE_URL + endpoint

  const headers = {
    Accept: 'application/json',
    Authorization: 'Bearer ' + useAuthStore().accessToken,
    'Content-Type': 'application/json'
  }
  try {
    const data = await axios.patch(url, content, {
      headers: headers
    })
    return data
  } catch (e) {
    console.error(e)
  }
}

export async function getConsumptionRequestV1(endpoint, cancelPendingRequest) {
  const url = globalThis.appConfig.VITE_API_BASE_URL + endpoint

  if (!cancelPendingRequest) {
    try {
      const data = await axios.get(url, {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + useAuthStore().accessToken,
          'Content-Type': 'application/json'
        }
      })
      return data
    } catch (e) {
      console.error(e)
      return [0]
    }
  } else if (cancelPendingRequest) {
    try {
      const code = hashCode(endpoint.substring(0, endpoint.indexOf('?')))
      if (cancel[code]) {
        cancel[code].abort('Intercept earlier request')
      }
      const controller = new AbortController()
      cancel[code] = controller
      const data = await axios.get(url, {
        headers: {
          Authorization: 'Bearer ' + useAuthStore().accessToken
        },
        signal: controller.signal
      })
      return data
    } catch (e) {
      console.error(e)
    }
  }
}

export function postRequestv1(endpoint, body, signal: AbortSignal, timeOut) {
  const url = globalThis.appConfig.VITE_API_BASE_URL + endpoint

  const headers = {
    Authorization: 'Bearer ' + useAuthStore().accessToken,
    'Content-Type': 'application/json'
  }
  axios.defaults.timeout = 0
  if (timeOut) axios.defaults.timeout = timeOut
  return axios.post(url, body, {
    headers,
    signal
  })
}
